import DOCUMENTS_SNAPSHOT from '../../../../rental-portal-mock-data/documents.json'
import USERS_SNAPSHOT from '../../../../rental-portal-mock-data/users.json'
import { HTTP_STATUS_INTERNAL_SERVER_ERROR } from '../../../constants/api'
import { EMPTY_LIST } from '../../../constants/common'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
} from '../../../constants/rentalPortalMockState'
import { hasRentalPortalMockState } from '../../../utils/rentalPortalMockState'
import { toMockSessionUser } from '../../RentalPortalListing'

const MOCK_VIEWING_DOCUMENTS_READ_LATENCY = 300

const MOCK_VIEWING_DOCUMENTS_UPLOAD_LATENCY = 900

const MOCK_VIEWING_DOCUMENTS_READ_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_VIEWING_DOCUMENTS_SAVE_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_VIEWING_DOCUMENT_UPLOAD_ERROR = {
  isPersistent: true,
  text: 'RentalPortal.ViewingDocumentUploadFileErrorKey',
}

const MOCK_VIEWING_DOCUMENT_FIRST_ID = 90000

const MOCK_ADDED_VIEWING_DOCUMENTS = new Map()

let mockViewingDocumentId = MOCK_VIEWING_DOCUMENT_FIRST_ID

const MOCK_USER_BY_ID = new Map(USERS_SNAPSHOT.map(user => [user.id, user]))

function toMockViewingDocumentsById(documents) {
  return [...documents]
    .sort((first, second) =>
      second.uploaded_at.localeCompare(first.uploaded_at)
    )
    .reduce((acc, document) => {
      const served = acc.get(document.viewing_id) ?? EMPTY_LIST

      return acc.set(document.viewing_id, [
        ...served,
        {
          ...document,
          uploaded_by:
            MOCK_USER_BY_ID.get(document.uploaded_by?.id) ??
            document.uploaded_by,
        },
      ])
    }, new Map())
}

const MOCK_VIEWING_DOCUMENTS_BY_ID =
  toMockViewingDocumentsById(DOCUMENTS_SNAPSHOT)

function withMockLatency(latency = MOCK_VIEWING_DOCUMENTS_READ_LATENCY) {
  const delay = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading)
    ? RENTAL_PORTAL_MOCK_LATENCY
    : latency

  return new Promise(resolve => {
    setTimeout(resolve, delay)
  })
}

function getServedMockViewingDocuments(viewingId) {
  return [
    ...(MOCK_ADDED_VIEWING_DOCUMENTS.get(Number(viewingId)) ?? EMPTY_LIST),
    ...(MOCK_VIEWING_DOCUMENTS_BY_ID.get(Number(viewingId)) ?? EMPTY_LIST),
  ]
}

export function getMockViewingDocuments({
  page,
  page_size: pageSize,
  viewing_id: viewingId,
}) {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_VIEWING_DOCUMENTS_READ_ERROR)
    }

    const documents = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
      ? EMPTY_LIST
      : getServedMockViewingDocuments(viewingId)

    const pageCount = Math.ceil(documents.length / pageSize)
    const currentPage = Math.min(page, Math.max(pageCount, 1))
    const firstIndex = (currentPage - 1) * pageSize

    return {
      data: {
        pagination: {
          page: currentPage,
          page_count: pageCount,
          total: documents.length,
        },
        results: {
          objects: documents.slice(firstIndex, firstIndex + pageSize),
        },
      },
    }
  })
}

export function getMockViewingDocumentUploads({ files }) {
  return withMockLatency(MOCK_VIEWING_DOCUMENTS_UPLOAD_LATENCY).then(() =>
    files.map(file => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return { ...file, error: MOCK_VIEWING_DOCUMENT_UPLOAD_ERROR }
      }

      mockViewingDocumentId += 1

      return { ...file, id: mockViewingDocumentId, uploading: false }
    })
  )
}

export function postMockViewingDocuments({
  currentUser,
  files,
  viewing_id: viewingId,
}) {
  return withMockLatency(MOCK_VIEWING_DOCUMENTS_UPLOAD_LATENCY).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveConflict)) {
      return Promise.reject(MOCK_VIEWING_DOCUMENTS_SAVE_ERROR)
    }

    const documents = files.map(file => ({
      id: file.id,
      mime_type: file.mime_type,
      name: file.name,
      size_bytes: file.size,
      uploaded_at: new Date().toISOString(),
      uploaded_by: toMockSessionUser(currentUser),
      url: null,
      viewing_id: Number(viewingId),
    }))

    MOCK_ADDED_VIEWING_DOCUMENTS.set(Number(viewingId), [
      ...documents,
      ...(MOCK_ADDED_VIEWING_DOCUMENTS.get(Number(viewingId)) ?? EMPTY_LIST),
    ])

    return { data: { results: { objects: documents } } }
  })
}
